import ExperianLogo from 'assets/svgs/experian-logo.svg';
import Images from 'assets/images';
import MoodysLogo from 'assets/svgs/moodys-logo.svg';

export const CreditScoreType = Object.freeze({
  creditScoreNone: '0',
  creditScoreA: '1',
  creditScoreB: '2',
  creditScoreC: '3',
  creditScoreD: '4',
  creditScoreE: '5',
  creditScoreF: '6',
  creditScoreG: '7',
  creditScoreH: '8',
});

export const CreditScoreMembership = Object.freeze({
  actual: 'actual',
  band: 'band',
});

export const CreditScoreName = Object.freeze({
  good: 'Good',
  medium: 'Okay',
  poor: 'Low',
});

export const CreditScoreLetter = Object.freeze({
  [CreditScoreType.creditScoreA]: 'A',
  [CreditScoreType.creditScoreB]: 'B',
  [CreditScoreType.creditScoreC]: 'C',
  [CreditScoreType.creditScoreD]: 'D',
  [CreditScoreType.creditScoreE]: 'E',
  [CreditScoreType.creditScoreF]: 'F',
  [CreditScoreType.creditScoreG]: 'G',
  [CreditScoreType.creditScoreH]: 'H',
  [CreditScoreType.creditScoreNone]: 'None',
});

export const CreditScoreFromLetterToType = Object.freeze({
  A: CreditScoreType.creditScoreA,
  B: CreditScoreType.creditScoreB,
  C: CreditScoreType.creditScoreC,
  D: CreditScoreType.creditScoreD,
  E: CreditScoreType.creditScoreE,
  F: CreditScoreType.creditScoreF,
  G: CreditScoreType.creditScoreG,
  H: CreditScoreType.creditScoreH,
  None: CreditScoreType.creditScoreNone,
});

export const CreditScoreColor = Object.freeze({
  [CreditScoreType.creditScoreA]: 'green-600',
  [CreditScoreType.creditScoreB]: 'green-400',
  [CreditScoreType.creditScoreC]: 'green-300',
  [CreditScoreType.creditScoreD]: 'warning-300',
  [CreditScoreType.creditScoreE]: 'warning-500',
  [CreditScoreType.creditScoreF]: 'danger-600',
  [CreditScoreType.creditScoreG]: 'danger-600',
  [CreditScoreType.creditScoreH]: 'danger-600',
});

export const CreditScoreBasicColor = Object.freeze({
  [CreditScoreType.creditScoreA]: 'green-400',
  [CreditScoreType.creditScoreB]: 'green-400',
  [CreditScoreType.creditScoreC]: 'warning-400',
  [CreditScoreType.creditScoreD]: 'warning-400',
  [CreditScoreType.creditScoreE]: 'danger-400',
  [CreditScoreType.creditScoreF]: 'danger-400',
  [CreditScoreType.creditScoreG]: 'danger-400',
  [CreditScoreType.creditScoreH]: 'danger-400',
});

export const CreditScoreRiskColor = Object.freeze({
  [CreditScoreType.creditScoreA]: 'green-50',
  [CreditScoreType.creditScoreB]: 'green-50',
  [CreditScoreType.creditScoreC]: 'warning-50',
  [CreditScoreType.creditScoreD]: 'warning-50',
  [CreditScoreType.creditScoreE]: 'danger-50',
  [CreditScoreType.creditScoreF]: 'danger-50',
});

export const CreditScoreTypeLabel = Object.freeze({
  [CreditScoreType.creditScoreA]: 'A (Good)',
  [CreditScoreType.creditScoreB]: 'B (Good)',
  [CreditScoreType.creditScoreC]: 'C (Good)',
  [CreditScoreType.creditScoreD]: 'D (Okay)',
  [CreditScoreType.creditScoreE]: 'E (Okay)',
  [CreditScoreType.creditScoreF]: 'F (Poor)',
  [CreditScoreType.creditScoreG]: 'G (Poor)',
  [CreditScoreType.creditScoreH]: 'H (Poor)',
});

export const CreditScoreRisk = Object.freeze({
  [CreditScoreType.creditScoreA]: 'Minimum Risk',
  [CreditScoreType.creditScoreB]: 'Low Risk',
  [CreditScoreType.creditScoreC]: 'Below Average Risk',
  [CreditScoreType.creditScoreD]: 'Above Average Risk',
  [CreditScoreType.creditScoreE]: 'High Risk',
  [CreditScoreType.creditScoreF]: 'Maximum Risk',
  [CreditScoreType.creditScoreG]: 'Maximum Risk',
  [CreditScoreType.creditScoreH]: 'Maximum Risk',
});

const CREDIT_RISK_LOW_TEXT = 'Low risk';
const CREDIT_RISK_AVERAGE_TEXT = 'Average risk';
const CREDIT_RISK_HIGH_TEXT = 'High risk';

export const CREDIT_SCORE_POOR = 'Low';
export const CREDIT_SCORE_AVERAGE = 'Average';
export const CREDIT_SCORE_GOOD = 'Good';

export const BasicCreditScore = Object.freeze({
  [CREDIT_SCORE_GOOD]: 100,
  [CREDIT_SCORE_AVERAGE]: 55,
  [CREDIT_SCORE_POOR]: 33,
});

const CREDIT_RISK_LOW_DESCRIPTION =
  ', as there is a low probability of the company going into default in the next 12 months. Offering credit terms to the recommended credit limit is advised.';
const CREDIT_RISK_AVERAGE_DESCRIPTION =
  ', as there is a moderate probability of the company going into default in the next 12 months. Offering credit terms above the recommended credit limit is advised against.';
const CREDIT_RISK_HIGH_DESCRIPTION =
  ', as there is a high probability of the company going into default in the next 12 months. Offering credit terms is strongly advised against.';

export const CreditScoreRiskLevel = Object.freeze({
  [CreditScoreType.creditScoreA]: CREDIT_RISK_LOW_TEXT,
  [CreditScoreType.creditScoreB]: CREDIT_RISK_LOW_TEXT,
  [CreditScoreType.creditScoreC]: CREDIT_RISK_AVERAGE_TEXT,
  [CreditScoreType.creditScoreD]: CREDIT_RISK_AVERAGE_TEXT,
  [CreditScoreType.creditScoreE]: CREDIT_RISK_HIGH_TEXT,
  [CreditScoreType.creditScoreF]: CREDIT_RISK_HIGH_TEXT,
  [CreditScoreType.creditScoreG]: CREDIT_RISK_HIGH_TEXT,
  [CreditScoreType.creditScoreH]: CREDIT_RISK_HIGH_TEXT,
});

export const CreditScoreRiskDescription = Object.freeze({
  [CreditScoreType.creditScoreA]: CREDIT_RISK_LOW_DESCRIPTION,
  [CreditScoreType.creditScoreB]: CREDIT_RISK_LOW_DESCRIPTION,
  [CreditScoreType.creditScoreC]: CREDIT_RISK_AVERAGE_DESCRIPTION,
  [CreditScoreType.creditScoreD]: CREDIT_RISK_AVERAGE_DESCRIPTION,
  [CreditScoreType.creditScoreE]: CREDIT_RISK_HIGH_DESCRIPTION,
  [CreditScoreType.creditScoreF]: CREDIT_RISK_HIGH_DESCRIPTION,
  [CreditScoreType.creditScoreG]: CREDIT_RISK_HIGH_DESCRIPTION,
  [CreditScoreType.creditScoreH]: CREDIT_RISK_HIGH_DESCRIPTION,
});

export const CREDIT_SCORE_INTRO = 'Your profile is considered as ';
export const CREDIT_SCORE_INTRO_RELATED_COMPANY = 'This profile is considered as ';
const CREDIT_SCORE_LOW_TEXT =
  ' to banks and lenders. Access to finance will be challenging and a personal guarantee will be required.';
const CREDIT_SCORE_MEDIUM_TEXT =
  ' to banks and lenders. Access to finance at your credit limit is likely, higher values may require a guarantee.';
const CREDIT_SCORE_HIGH_TEXT =
  ' to banks and lenders. Access to finance is highly likely at your credit limit value, with favourable rates.';

export const RISK_SUPPRESSED = 'Risk score suppressed';
export const NO_SCORE = 'No score';
export const NO_CREDIT_SCORE = 'No credit score';
export const RISK_OF_FAILURE = 'Risk of failure';
export const SERIOUS_ADVERSE_INFORMATION = 'Serious Adverse Information';

export const CreditScoreTextByGrade = Object.freeze({
  [CreditScoreType.creditScoreA]: CREDIT_SCORE_HIGH_TEXT,
  [CreditScoreType.creditScoreB]: CREDIT_SCORE_HIGH_TEXT,
  [CreditScoreType.creditScoreC]: CREDIT_SCORE_MEDIUM_TEXT,
  [CreditScoreType.creditScoreD]: CREDIT_SCORE_MEDIUM_TEXT,
  [CreditScoreType.creditScoreE]: CREDIT_SCORE_LOW_TEXT,
  [CreditScoreType.creditScoreF]: CREDIT_SCORE_LOW_TEXT,
});

export function toOptions() {
  return Object.entries(CreditScoreTypeLabel).map(([value, label]) => ({ label, value }));
}

export const CreditScoreProvider = Object.freeze({
  creditSafe: 'creditSafe',
  experian: 'Experian',
  bvdOrbis: 'bvdOrbis',
});

export const CreditScoreProviderLogo = Object.freeze({
  [CreditScoreProvider.experian]: ExperianLogo,
  [CreditScoreProvider.bvdOrbis]: MoodysLogo,
});

export const CreditScoreProviderLogoUrl = Object.freeze({
  [CreditScoreProvider.experian]: Images.experianLogo,
  [CreditScoreProvider.bvdOrbis]: Images.moodysLogo,
});

export const CreditScoreGrades = Object.freeze({
  good: [CreditScoreType.creditScoreA, CreditScoreType.creditScoreB, CreditScoreType.creditScoreC],
  medium: [CreditScoreType.creditScoreD, CreditScoreType.creditScoreE],
  poor: [CreditScoreType.creditScoreF, CreditScoreType.creditScoreG, CreditScoreType.creditScoreH],
});

export const CreditScoreDefaultOptions = Object.freeze(
  Object.values(CreditScoreType)
    .filter((score) => score !== CreditScoreType.creditScoreNone)
    .map((score) => ({
      title: CreditScoreLetter[score],
      color: CreditScoreColor[score],
      value: CreditScoreLetter[score],
    })),
);

export const CreditRiskFundingGrades = Object.freeze({
  highRisk: 'HIGH_RISK',
  higherRisk: 'HIGHER_RISK',
  relativelyStrong: 'RELATIVELY_STRONG',
  lowRisk: 'LOW_RISK',
});

const CREDIT_SCORE_RISK_HIGH_TEXT =
  'Your business has a high risk credit score. It is unlikely you will get a loan but you may be eligible for a business credit card.';
const CREDIT_SCORE_RISK_HIGHER_TEXT =
  'Your business has a higher risk credit score. Consider a credit review service before applying for funding?';
const CREDIT_SCORE_RISK_STRONG_TEXT =
  'Your business has a relatively strong credit score. Lenders are likely to consider your application.';
const CREDIT_SCORE_RISK_LOW_TEXT =
  'Your business has  a low risk credit score. This is great when reviewed by banks and lenders.';

export const CreditRiskTextByGrade = Object.freeze({
  [CreditRiskFundingGrades.highRisk]: CREDIT_SCORE_RISK_HIGH_TEXT,
  [CreditRiskFundingGrades.higherRisk]: CREDIT_SCORE_RISK_HIGHER_TEXT,
  [CreditRiskFundingGrades.relativelyStrong]: CREDIT_SCORE_RISK_STRONG_TEXT,
  [CreditRiskFundingGrades.lowRisk]: CREDIT_SCORE_RISK_LOW_TEXT,
});
